import React, { useMemo } from "react";
import { gql, useQuery } from "urql";
import { cloneDeep } from "lodash";
import { Link, useSearchParams } from "wouter";

import { SelectColumnFilter } from "../../../../components/src/react-table/ReactTableFilters";
import { ReactTable } from "../../../../components/src/react-table/ReactTable";
import { exportToXlsx2 } from "../../../../components/src/common/exportToXlsx2";

const getTimeseries = gql`
    query getTimeseries($filter: TimeSeriesFilterInput) {
        timeseries(includeItems: false, filter: $filter) {
            _id
            status
            clientId
            client {
                _id
                name
            }
            objectId
            instrument {
                _id
                name
                longName
                productType
                currency
            }
            type
            status
            updateTimestamp
        }
    }
`;

export const TimeSeriesPage = (): React.ReactElement => {
    const [queryArgs] = useSearchParams();
    const instrumentId = queryArgs.get("instrumentId");

    // instrumentId queryArg
    const filter = instrumentId ? { instrumentIdIn: [instrumentId] } : {};
    const [{ fetching, error, data }] = useQuery({ query: getTimeseries, variables: { filter } });

    const columns = useMemo(
        () => [
            {
                header: "Id",
                accessorKey: "_id",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/timeseries/${row.original._id}`}>{cellProps.getValue()}</Link>;
                },
                size: 100
            },
            /* TODO: All clientId seems to be set to 24x0. We should correct that in backend and frontend
            {
                Header: "Client",
                accessor: "clientName",
                minWidth: 50,
                width: 200,
                Cell: (cellProps) => {
                    const { row, value } = cellProps;
                    if (row.original.client) {
                        return (
                            <Link to={`/parties/${row.original.client._id}`} >
                                {value}
                            </Link>
                        );
                    } else {
                        return null;
                    }
                }
            },*/
            {
                header: "Instrument",
                id: "instrumentName",
                accessorKey: "instrumentName",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return row.original.instrument ? (
                        <Link to={`/instruments/${row.original.instrument._id}`}>{cellProps.getValue()}</Link>
                    ) : (
                        <div>{cellProps.getValue()}</div>
                    );
                }
            },
            {
                header: "Long name",
                id: "instrument.longName",
                accessorKey: "instrument.longName"
            },
            {
                header: "Type",
                accessorKey: "type"
            },
            {
                header: "Product type",
                accessorKey: "instrumentProductType",
                filter: SelectColumnFilter,
                size: 75
            },
            {
                header: "Status",
                accessorKey: "status",
                size: 75
            },
            {
                header: "Currency",
                id: "instrument.currency",
                accessorKey: "instrument.currency",
                filter: SelectColumnFilter,
                size: 75
            },
            {
                header: "Update timestamp",
                accessorKey: "updateTimestamp"
            }
        ],
        []
    );

    const download = (timeseries) => {
        const rows = timeseries.map((t) => {
            return {
                _id: t._id,
                instrument: t.instrumentName,
                longName: t.instrument.longName,
                type: t.type,
                productType: t.instrumentProductType,
                status: t.status,
                currency: t.instrument.currency,
                updateTimestamp: t.updateTimestamp
            };
        });
        exportToXlsx2(rows, "timeseries.xlsx");
    };

    if (fetching) return <p>Loading</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;

    const timeseries = cloneDeep(data.timeseries);

    timeseries.forEach((timeserie) => {
        timeserie.clientName = timeserie.client ? (timeserie.client.name ? timeserie.client.name : "") : "";
        timeserie.instrumentName = timeserie.instrument ? (timeserie.instrument.name ? timeserie.instrument.name : "") : "";
        timeserie.instrumentProductType = timeserie.instrument
            ? timeserie.instrument.productType
                ? timeserie.instrument.productType
                : ""
            : "";
    });

    return (
        <div>
            <div className="flex">
                <div className="ml-auto p-2">
                    <Link to={"/timeseries/new"}>New</Link>
                </div>
            </div>
            <p>Number of time series: {timeseries.length}</p>
            <ReactTable
                columns={columns}
                data={timeseries}
                defaultHiddenColumns={["status", "updateTimestamp"]}
                onExport={() => download(timeseries)}
            />
        </div>
    );
};
